/**
 * The settings tab strip, decided away from the markup.
 *
 * Salesforge draws Settings as one page with a strip of tabs under the title
 * (Usage, Users & Roles, Integrations, DNC list, API & MCP, Notifications) and
 * no rail beside it. Ours is the same page with our own screens in the strip,
 * in the reference's order first and ours after: Usage, Users & Roles,
 * Integrations, DNC list, API, Notifications, then Safety & limits, Brands,
 * Variables, Lead magnets and Activity log. There is no `More` menu any more.
 * The strip scrolls sideways when the tabs do not fit rather than wrapping, so
 * nothing is hidden behind a second click.
 *
 * ONE TAB IS NOT IN THAT ORDER. Prospecting stands beside Integrations, where
 * it has stood since it became a tab (2026-10-05): the settings of the
 * prospects pages. Until 2026-10-24 the tab handed over to the old app's own
 * settings page, drawn with a copy of this strip that drifted from it. It is
 * a screen of this app now, at `/settings/prospecting`, and the browser
 * helper's setup is a page under it that keeps the tab lit.
 *
 * It is the one tab whose audience is not the admin role. An admin is an owner
 * there and sees every setting, and a member with a seat sees their own, so
 * the tab is offered to admins and to anybody GET /me says has a seat
 * (`prospecting` below).
 *
 * OPERATOR IS OFFERED ON THE SERVICE'S OWN ROLE (2026-10-30,
 * docs/SON-OF-BASEL-MERGE.md): the settings of what was the Son of Basel
 * portal, the clone's agent, its prompts, models, voice and face, at
 * `/settings/operator`, with the portal's admin pages under it at
 * `/settings/operator/admin`. It stands after Prospecting. Both screens are
 * for Operator admins only (the portal kept `/settings` and `/admin` to its
 * admins), so a member with a seat would only read "This page is for admins"
 * there: the tab is offered to admins here and to anybody GET /me on the
 * Operator service says is an admin there (`operator` below, which the
 * caller reads as `useOperatorReach().admin`).
 *
 * Every path is the one it always was, so a bookmark, a runbook line or an old
 * message still lands on the same screen, now with its tab lit.
 *
 * TWO PATHS LIGHT A TAB THAT IS NOT THEIR OWN. `/settings/activity/live` is the
 * live feed, which lives inside the Activity log, and `/settings/engine` is the
 * device engine's tokens and reports, which is reached from a card on the
 * Integrations tab, the way Salesforge keeps every connected tool and its key
 * there. Neither gets a tab of its own, and neither leaves the strip dark.
 *
 * WHAT IT DOES NOT DO: it does not gate anything. `adminOnly` hides a tab from
 * somebody the route would refuse, which is a courtesy. The routes keep their
 * own `RequireAdmin` and the API keeps its own check, and only the last one is
 * load bearing.
 *
 * Nothing here imports React, so it is covered by a plain unit test.
 */

import { AI_ARK_PUT_AWAY } from '@gojibasel/shared/ai-ark'

export interface SettingsTab {
  to: string
  label: string
  /** Match the path exactly. Only the index route needs it. */
  end?: boolean
  adminOnly?: boolean
  /** Other paths that light this tab, because they are reached from it. */
  also?: readonly string[]
  /** What the tab holds, behind the `(?)` beside its label. */
  help?: string
  /** Offered to admins, and to anybody with a seat in Prospecting. */
  prospecting?: boolean
  /** Offered to admins, and to anybody the Operator service says is an admin there. */
  operator?: boolean
}

/**
 * The tabs, in the strip's order.
 *
 * Usage is the index route, so `/settings` lands on it, as Salesforge's bare
 * settings address does. Users & Roles and Integrations are admin only at the
 * route, so the tab hides for anybody the route would refuse. Prospecting
 * hides for anybody with no seat there, who would get one sentence saying so.
 * Notifications is Salesforge's sixth tab and ours, open to everybody, because
 * it holds the alert emails of the person signed in.
 */
export const SETTINGS_TABS: readonly SettingsTab[] = [
  {
    to: '/settings',
    label: 'Usage',
    end: true,
    help: 'What you have used today against what you are allowed.',
  },
  {
    to: '/settings/people',
    label: 'Users & Roles',
    adminOnly: true,
    help: 'Who can sign in, their role, and the brands they belong to.',
  },
  {
    to: '/settings/integrations',
    label: 'Integrations',
    adminOnly: true,
    also: ['/settings/engine'],
    help: 'Where alerts go, and the tools connected to this workspace.',
  },
  {
    // The prospects pages' settings, drawn here (pages/prospecting/settings).
    // For admins and seat holders, as the Prospecting rows in the sidebar are.
    to: '/settings/prospecting',
    label: 'Prospecting',
    prospecting: true,
    help: 'Reply drafts, people and brands, the writing schedule and everything the searches find.',
  },
  {
    // Operator's settings, drawn here (pages/operator/settings), with the
    // admin page under it keeping the tab lit. For admins here and Operator
    // admins there: both screens refuse anybody else.
    to: '/settings/operator',
    label: 'Operator',
    operator: true,
    help: 'The clone and its prompts, models, voice and face, the meetings it joins, and who may use it.',
  },
  {
    // Operator > Companies: the company and people registry, when it runs,
    // its budget, sources and house rules (pages/operator/companies/settings).
    // Admin only at the route and on the server, like the pages themselves.
    to: '/settings/operator-companies',
    label: 'Operator companies',
    adminOnly: true,
    help: 'The companies and people the Operator works for, when it runs, what it may spend, its sources and its house rules.',
  },
  {
    // Discovery's sources, server keys, n8n webhooks and limits (2026-10-26,
    // docs/DISCOVERY.md). Beside Prospecting, the other finding work. Admin
    // only at the route and on the server, like Discovery itself.
    to: '/settings/discovery',
    label: 'Discovery',
    adminOnly: true,
    help: 'Which sources Discovery may use, the keys they need, your n8n webhooks and the limits every check works inside.',
  },
  // AI Ark (docs/AI-ARK-HANDOVER.md): the settings row (prices, limits per
  // run and per day, the balance floor, the switches) and the spend. Beside
  // Discovery, whose tab it lives under. Admin only at the route and on the
  // server, like the tab itself. Left out while AI Ark is put away (2026-11-04).
  ...(AI_ARK_PUT_AWAY
    ? []
    : [
        {
          to: '/settings/ai-ark',
          label: 'AI Ark',
          adminOnly: true,
          help: 'What AI Ark may spend per run and per day, the balance it must leave, its switches, and what it has spent.',
        },
      ]),
  {
    // Which Claude model each kind of call uses (0162). Admin only at the
    // route and on the server, because one choice covers every brand.
    to: '/settings/ai-models',
    label: 'AI models',
    adminOnly: true,
    help: 'Which Claude model labels replies, writes drafts and writes the reasons on file.',
  },
  {
    to: '/settings/suppressions',
    label: 'DNC list',
    help: 'Addresses, domains and profiles that are never contacted.',
  },
  { to: '/settings/api', label: 'API' },
  { to: '/settings/notifications', label: 'Notifications' },
  { to: '/settings/safety', label: 'Safety & limits' },
  { to: '/settings/brands', label: 'Brands' },
  { to: '/settings/variables', label: 'Variables' },
  { to: '/settings/lead-magnets', label: 'Lead magnets' },
  { to: '/settings/activity', label: 'Activity log' },
]

/**
 * The tabs this person may open. `prospecting` is whether they have a seat in
 * Prospecting, which an admin here always has. `operator` is whether Operator
 * says they are an admin there (`useOperatorReach().admin`, the stored Son of
 * Basel role), and only that: an admin here who is a member in Operator does
 * not see its settings, exactly as the old portal never showed them a member.
 */
export function tabsFor(isAdmin: boolean, options: { prospecting?: boolean; operator?: boolean } = {}): SettingsTab[] {
  return SETTINGS_TABS.filter((tab) => {
    if (tab.adminOnly === true && !isAdmin) return false
    if (tab.prospecting === true && !isAdmin && options.prospecting !== true) return false
    if (tab.operator === true && options.operator !== true) return false
    return true
  })
}

/**
 * Whether a path is this screen or one of its children.
 *
 * On a segment boundary, so `/settings/activity/live` is the activity log's
 * and `/settings/api-old` would not be the API tab's.
 */
export function pathIsUnder(pathname: string, to: string): boolean {
  const path = trimPath(pathname)
  return path === to || path.startsWith(`${to}/`)
}

function trimPath(pathname: string): string {
  return pathname.replace(/\/+$/, '') || '/'
}

/**
 * The tab that is lit for this path, by its `to`, or null when none is.
 *
 * The index tab only on its own path, so it does not stay lit under every
 * child. Otherwise the longest match wins, so a child path names its own tab
 * and not a shorter one that happens to prefix it, and the paths a tab lists
 * under `also` count as its own.
 */
export function activeSettingsTab(pathname: string, tabs: readonly SettingsTab[]): string | null {
  const path = trimPath(pathname)
  let best: { to: string; length: number } | null = null
  for (const tab of tabs) {
    if (tab.end === true) {
      if (path === tab.to) return tab.to
      continue
    }
    for (const candidate of [tab.to, ...(tab.also ?? [])]) {
      if (!pathIsUnder(path, candidate)) continue
      if (best === null || candidate.length > best.length) best = { to: tab.to, length: candidate.length }
    }
  }
  return best?.to ?? null
}
